import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { bottomReducers } from './bottom'
import { canonicalReducers } from './canonical'
import { globalConfigReducers } from './globalConfig'
import { topReducers } from './top'
import {
  createInitialPickerGroupState,
  DEFAULT_GROUP_ID,
  ensurePicker,
  getNextPickerId,
  initialState,
  PickerSliceState,
} from './types'

// ═══════════════════════════════════════════════════════════════════════════
// Slice
// ═══════════════════════════════════════════════════════════════════════════

const pickerSlice = createSlice({
  name: 'picker',
  initialState,
  reducers: {
    // ═══════════════════════════════════════════════════════════════════════════
    // Group Lifecycle
    // ═══════════════════════════════════════════════════════════════════════════

    initializeGroup: (
      state: PickerSliceState,
      action: PayloadAction<{ groupId?: string }>
    ) => {
      const { groupId = DEFAULT_GROUP_ID } = action.payload
      state.groups[groupId] = createInitialPickerGroupState()
    },

    removeGroup: (
      state: PickerSliceState,
      action: PayloadAction<{ groupId?: string }>
    ) => {
      const { groupId = DEFAULT_GROUP_ID } = action.payload
      // Remove all pickers in this group
      for (const [pickerId, gId] of Object.entries(state.pickerToGroup)) {
        if (gId === groupId) {
          delete state.pickers[Number(pickerId)]
          delete state.pickerToGroup[Number(pickerId)]
        }
      }
      delete state.groups[groupId]
    },

    /**
     * Reset all picker state - clears all pickers and groups.
     * Use this when leaving the AI segment builder to prevent state leaks.
     */
    resetAllPickers: (state: PickerSliceState) => {
      state.pickers = {}
      state.pickerToGroup = {}
      state.groups = {}
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // Picker Lifecycle
    // ═══════════════════════════════════════════════════════════════════════════

    initializePicker: (
      state: PickerSliceState,
      action: PayloadAction<{ groupId?: string; pickerId: number }>
    ) => {
      const { groupId = DEFAULT_GROUP_ID, pickerId } = action.payload
      ensurePicker(state, pickerId, groupId)
    },

    removePicker: (
      state: PickerSliceState,
      action: PayloadAction<{ pickerId: number }>
    ) => {
      const { pickerId } = action.payload
      delete state.pickers[pickerId]
      delete state.pickerToGroup[pickerId]
    },

    setSubmitted: (
      state: PickerSliceState,
      action: PayloadAction<{ pickerId: number; isSubmitted: boolean }>
    ) => {
      const { pickerId, isSubmitted } = action.payload
      const picker = state.pickers[pickerId]
      if (picker) {
        picker.isSubmitted = isSubmitted
      }
    },

    /**
     * Creates a new picker with an auto-generated ID.
     * Computes the next available ID from existing pickers.
     */
    createPicker: (
      state: PickerSliceState,
      action: PayloadAction<{ groupId?: string }>
    ) => {
      const { groupId = DEFAULT_GROUP_ID } = action.payload
      const pickerId = getNextPickerId(state)
      ensurePicker(state, pickerId, groupId)
    },

    // Import reducers from sub-files
    ...topReducers,
    ...bottomReducers,
    ...globalConfigReducers,
    ...canonicalReducers,
  },
})

// ═══════════════════════════════════════════════════════════════════════════
// Actions
// ═══════════════════════════════════════════════════════════════════════════

export const {
  // Group Lifecycle
  initializeGroup,
  removeGroup,
  resetAllPickers,
  // Picker Lifecycle
  initializePicker,
  removePicker,
  setSubmitted,
  createPicker,
  // Top
  loadTopData,
  setTopSearchTerm,
  setTopPage,
  setTopTotalCount,
  setTopSelectedItem,
  clearTopSelection,
  // Top Configuration
  setTopLabel,
  setTopOperator,
  setTopOperatorOptions,
  setTopEndpointConfig,
  // Top UI State
  setTopIsOpen,
  toggleTopIsOpen,
  openTop,
  closeTop,
  // Bottom
  loadBottomData,
  setBottomSearchTerm,
  setBottomPage,
  setBottomTotalCount,
  addBottomSelectedItem,
  removeBottomSelectedItem,
  setBottomSelectedItems,
  clearBottomSelection,
  selectAllBottomOnPage,
  deselectAllBottomOnPage,
  // Bottom Configuration
  setBottomLabel,
  setBottomOperator,
  setBottomOperatorOptions,
  setBottomEndpointConfig,
  setBottomSubtitle,
  // Bottom UI State
  toggleBottomShowingSelections,
  setBottomShowingSelections,
  setBottomDisabled,
  // Bottom Open/Close
  setBottomIsOpen,
  toggleBottomIsOpen,
  openBottom,
  closeBottom,
  // Group Config (all config is per-group now)
  setGroupConfig,
  setGroupLevel1Config,
  setGroupLevel2Config,
  resetGroupConfig,
  setGroupPickerId,
  setGroupTopOperator,
  setGroupDisabled,
  setGroupTitle,
  setGroupSubtitle,
  setGroupSingleLevel,
  // Canonical levels
  createCanonicalPicker,
  setCanonicalPicks,
  describeCanonicalPicks,
  failToDescribeCanonicalPicks,
  scopeCanonicalPicks,
  failToScopeCanonicalPicks,
  toggleCanonicalPick,
  switchCanonicalSource,
  switchToDefaultSource,
} = pickerSlice.actions

// Legacy aliases for backward compatibility (deprecated - use group-level actions)
export const setGlobalTopOperator = setGroupTopOperator
export const setGlobalConfig = setGroupConfig
export const setGlobalLevel1Config = setGroupLevel1Config
export const setGlobalLevel2Config = setGroupLevel2Config
export const resetGlobalConfig = resetGroupConfig

// ═══════════════════════════════════════════════════════════════════════════
// Re-export selectors from sub-files
// ═══════════════════════════════════════════════════════════════════════════

export {
  selectTop,
  selectTopData,
  selectTopSelectedItem,
  selectTopSelectedItemData,
  selectTopSearchTerm,
  selectTopPage,
  selectTopTotalCount,
  selectTopPageSize,
  selectTopLabel,
  selectTopOperator,
  selectTopOperatorOptions,
  selectTopEndpointConfig,
  selectTopIsOpen,
  selectPickerGroupId,
} from './top'

export {
  selectBottom,
  selectBottomData,
  selectBottomSelectedItems,
  selectBottomSelectedItemsCount,
  selectBottomSelectedItemsData,
  selectBottomSearchTerm,
  selectBottomPage,
  selectBottomTotalCount,
  selectBottomPageSize,
  selectBottomLabel,
  selectBottomOperator,
  selectBottomOperatorOptions,
  selectBottomEndpointConfig,
  selectBottomSubtitle,
  selectBottomShowingSelections,
  selectBottomDisabled,
  selectBottomIsOpen,
} from './bottom'

export {
  canonicalIdOf,
  picksOf,
  scopeOf,
  selectActiveSelection,
  selectActiveSource,
  selectCanonicalLevel,
  selectHasAnyCanonicalPick,
  startingLevel2Operator,
} from './canonical'

export {
  // New pickerId-based selectors (use pickerToGroup mapping)
  selectGroupConfig,
  selectLevel1Id,
  selectLevel1Label,
  selectLevel1OperatorOptions,
  selectLevel1EndpointConfig,
  selectLevel1KeyField,
  selectLevel1ValueField,
  selectLevel2Id,
  selectLevel2Label,
  selectLevel2OperatorOptions,
  selectLevel2EndpointConfig,
  selectLevel2Subtitle,
  selectLevel2KeyField,
  selectLevel2ValueField,
  sourceChoiceOf,
  SPECIFIC_SOURCE,
  // Group-level selectors (using groupId directly)
  selectGroupPickerId,
  selectGroupTopOperator,
  selectGroupDisabled,
  selectGroupTitle,
  selectGroupSubtitle,
  selectGroupLevel1Id,
  selectGroupLevel2Id,
  selectGroupLevelConfig,
  selectGroupLevel1Label,
  selectGroupLevel2Label,
  selectGroupLevel1OperatorOptions,
  selectGroupLevel2OperatorOptions,
  selectGroupSingleLevel,
  selectSingleLevel,
  // Deprecated legacy selectors (return defaults, for backward compatibility)
  selectGlobalConfig,
  selectGlobalTopOperator,
  selectGlobalLevel1Label,
  selectGlobalLevel1OperatorOptions,
  selectGlobalLevel1EndpointConfig,
  selectGlobalLevel1KeyField,
  selectGlobalLevel1ValueField,
  selectGlobalLevel2Label,
  selectGlobalLevel2OperatorOptions,
  selectGlobalLevel2EndpointConfig,
  selectGlobalLevel2Subtitle,
  selectGlobalLevel2KeyField,
  selectGlobalLevel2ValueField,
} from './globalConfig'

// ═══════════════════════════════════════════════════════════════════════════
// Memoized Selectors
// ═══════════════════════════════════════════════════════════════════════════

type RootState = { picker: PickerSliceState }

const selectGroupsMap = (state: RootState) => state.picker.groups
const selectPickersMap = (state: RootState) => state.picker.pickers

/**
 * Memoized selector for group IDs.
 * Only recomputes when the set of group IDs changes.
 */
export const selectGroupIds = createSelector([selectGroupsMap], groups =>
  Object.keys(groups)
)

/**
 * Memoized selector for all picker IDs.
 * Only recomputes when the set of IDs changes, not when picker contents change.
 */
export const selectPickerIds = createSelector([selectPickersMap], pickers =>
  Object.keys(pickers).map(Number)
)

/**
 * Selector for picker IDs in a specific group.
 */
export const selectPickerIdsForGroup = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): number[] => {
  const result: number[] = []
  for (const [pickerId, gId] of Object.entries(state.picker.pickerToGroup)) {
    if (gId === groupId) {
      result.push(Number(pickerId))
    }
  }
  return result
}

/**
 * Selector that computes the next available picker ID based on existing pickers.
 */
export const selectNextAvailablePickerId = (state: RootState): number => {
  const pickerIds = Object.keys(state.picker.pickers).map(Number)
  if (pickerIds.length === 0) return 0
  return Math.max(...pickerIds) + 1
}

/** @deprecated Use selectNextAvailablePickerId instead */
export const selectNextPickerId = selectNextAvailablePickerId

/**
 * Selector to check if any picker in a group has a top-level selection.
 * Used to determine if the Confirm button should be enabled (DATA-4313).
 */
export const selectHasAnyTopSelection = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): boolean => {
  const pickerIds = selectPickerIdsForGroup(state, groupId)
  for (const pickerId of pickerIds) {
    const picker = state.picker.pickers[pickerId]
    if (picker?.top.selectedItem !== null) {
      return true
    }
  }
  return false
}

/**
 * Selector to check if any picker in a group has any selection (top or bottom).
 * For single-level pickers, checks bottom selections; for two-level, checks top.
 */
export const selectHasAnySelection = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): boolean => {
  const singleLevel = state.picker.groups[groupId]?.singleLevel ?? false
  const pickerIds = selectPickerIdsForGroup(state, groupId)
  for (const pickerId of pickerIds) {
    const picker = state.picker.pickers[pickerId]
    if (singleLevel) {
      if (picker?.bottom.selectedItems.length > 0) return true
    } else {
      if (picker?.top.selectedItem !== null) return true
    }
  }
  return false
}

// ═══════════════════════════════════════════════════════════════════════════
// Re-export types and constants
// ═══════════════════════════════════════════════════════════════════════════

export { DEFAULT_GROUP_ID, filterOptionsBySearch } from './types'

export type { SourceChoice } from './globalConfig'

export type {
  CanonicalLevel,
  SourceSelection,
  DataItem,
  EndpointConfig,
  GlobalConfig,
  OperatorOption,
  TopDropdownState,
  BottomDropdownState,
  PickerState,
  PickerGroupState,
  PickerSliceState,
} from './types'

// ═══════════════════════════════════════════════════════════════════════════
// Reducer
// ═══════════════════════════════════════════════════════════════════════════

export const pickerReducer = pickerSlice.reducer
